import { accurateErrorsMessages, handleUnknownError } from "@shared/libs/oh-errors/error-messages";
import { OhApi } from "@shared/libs/siayuda";
import { DateTime, Duration } from "luxon";
import { ZodError } from "zod";

type FormFieldErrorValueBase = { type: string; verbose: string; extra?: Record<string, unknown> };
type FormFieldErrorEmptyValue = { message?: undefined; _errors?: undefined };
type FormFieldErrorValue = { message: string; _errors: FormFieldErrorValueBase[] };

export type FormFieldErrorObject<T extends Object> = {
  [K in keyof T]?: FormFieldError<NonNullable<T[K]>>;
};

// json type doesn't exist and object is too unacurate to be used here
// so we make a special case for all different types we can think of
// falling back to array and object types if it doesn't match
export type FormFieldError<V> = V extends string | number | Date | DateTime | Duration | boolean
  ? FormFieldErrorValue
  : (FormFieldErrorValue | FormFieldErrorEmptyValue) &
      (V extends (infer U)[]
        ? Record<number, FormFieldError<NonNullable<U>>>
        : FormFieldErrorObject<NonNullable<V>>);

export type FormGlobalError = {
  type: string;
  verbose: string;
  extra?: Record<string, any>;
};

export type ErrorMessages = Record<string, string | ((extra: Record<string, any>) => string)>;

export const getVerboseError = (
  type: string,
  extra?: Record<string, any>,
  customErrorMessages?: ErrorMessages
) => {
  const mergedErrorMessages = { ...accurateErrorsMessages, ...customErrorMessages };
  const errorMessage = mergedErrorMessages[type] || handleUnknownError(type);
  return typeof errorMessage === "function"
    ? extra
      ? errorMessage(extra)
      : handleUnknownError(type)
    : errorMessage;
};

/**
 * split a string key coming from a api client OhApi FieldErrorsWithExtra
 * into a list of sub keys
 * @param key a string key shaped as followed 'aaaa.bbbb[x].rrr'
 * @returns a list of sub keys (example : ['aaaa','bbbb',x,'rrr'])
 */
const splitErrorWithExtraKey = (key: string) => {
  return key.replace(/\]/g, "").split(/\.|\[/g);
};

/**
 * enrich provided json with a value according to provided keys path
 * create all missing nested keys in the process with an empty {}
 * @param json json to enriche with a new value
 * @param keys keys path to the new value
 * @param value the value to add to the json
 * @returns provided json with new value
 */
const enrichJsonWithNestedKey = <J extends {}>(
  json: J,
  [key, ...keys]: (string | number)[],
  value: any
): J => {
  if (!keys.length) {
    json[key] = value;
    return json;
  }
  if (!json.hasOwnProperty(key)) {
    json[key] = {};
  }
  return { ...json, [key]: { ...enrichJsonWithNestedKey(json[key], keys, value) } };
};

const getErrorMessage = (errors: FormFieldErrorValueBase[]) => {
  if (errors.length === 1) return errors[0].verbose;
  if (errors.length > 1) return errors.map(e => `• ${e.verbose}`).join("\n");
  return "";
};

export const computeFieldErrorValue = (
  errors: OhApi.FieldErrorWithExtra[],
  customErrorMessages: ErrorMessages = {}
) => {
  const verboseErrors = errors.map(e => ({
    ...e,
    verbose: getVerboseError(e.type, e.extra, customErrorMessages),
  }));
  return {
    message: getErrorMessage(verboseErrors),
    _errors: verboseErrors,
  };
};

/**
 * transform a fieldErrorsWithExtra into a FormErrorObject
 * @param errors client errors to format
 * @returns a FormErrorObject matching form data shape
 */
export const fieldErrorsWithExtraToFormError = <T>(
  errors: OhApi.FieldErrorsWithExtra,
  customErrorMessages?: ErrorMessages
) => {
  return Object.keys(errors)
    .sort()
    .reduce<FormFieldError<T>>((acc, key) => {
      const errorList = errors[key];
      const splittedKeys = splitErrorWithExtraKey(key);

      return enrichJsonWithNestedKey(
        acc,
        splittedKeys,
        computeFieldErrorValue(errorList, customErrorMessages)
      );
    }, {} as FormFieldError<T>);
};

/**
 * transform a zod error into a FormErrorObject
 * @param errors zod error to format
 * @returns a FormErrorObject matching form data shape
 */
export const zodErrorToFormError = <T>(errors: ZodError<T>) => {
  const groupedErrors = errors.errors
    // group errors by joined path
    .reduce<Record<string, FormFieldErrorValueBase[]>>((acc, error) => {
      const path = error.path.join(".");
      if (!acc[path]) {
        acc[path] = [];
      }
      acc[path].push({ type: error.code, verbose: error.message });
      return acc;
    }, {});

  return Object.keys(groupedErrors)
    .sort()
    .reduce<FormFieldError<T>>((acc, key) => {
      const errorList = groupedErrors[key];
      const splittedKeys = splitErrorWithExtraKey(key);

      return enrichJsonWithNestedKey(acc, splittedKeys, {
        message: getErrorMessage(errorList),
        _errors: errorList,
      });
    }, {} as FormFieldError<T>);
};

export const globalErrorToFormError = (
  error: OhApi.GlobalError,
  customErrorMessages?: ErrorMessages
): FormGlobalError => {
  return {
    type: error.error,
    verbose: getVerboseError(error.error, error.extra, customErrorMessages),
    extra: error.extra,
  };
};
